import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import ReactDOM from 'react-dom';
import { withTheme } from '../core/theming';

import Div from './Div';
import Input from './Input';
import Icon from './Icon';
import Tag from './Tag';
import Button from './Button';

const findAutocompleteDir = (e) => {
	switch (e) {
		case 'topright':
			return 'transform-origin: 100% 100%; bottom: calc(100% + 2px); right: 0;';
		case 'topleft':
			return 'transform-origin: 0 100%; bottom: calc(100% + 2px); left: 0;';
		case 'bottomright':
			return 'transform-origin: 50% 0; top: calc(100% + 2px); right: 0;';
		case 'righttop':
			return 'transform-origin: 0 0; top: 0; left: calc(100% + 2px);';
		case 'lefttop':
			return 'transform-origin: 0 0; top: 0; right: calc(100% + 2px);';
		default:
			return 'transform-origin: 50% 0; top: calc(100% + 2px); left: 0;';
	}
};

const BaseAutocomplete = styled(Div)`
	> div:last-child {
		position: absolute;
		z-index: 10;
		${(props) => findAutocompleteDir(props.direction)}
		transition: all 0.2s ease-in-out;
		opacity: ${(props) => (props.isOpen ? '1' : '0')};
		transform: scaleY(${(props) => (props.isOpen ? '1' : '0')});
	}

	${(props) => props.targetHover && `:hover { border-color: ${props.theme.colors[props.focusBorderColor]}}`}
	${(props) => props.targetHover && ':hover > div:last-child{ opacity: 1; transform: scaleY(1);}'}
`;

class Autocomplete extends Component {
	constructor(props) {
		super(props);

		this.state = {
			selectedValue: props.value || props.defaultValue,
			selectedIndex:
				props.menu.findIndex((item) => (props.value || props.defaultValue) === item) > -1
					? props.menu.findIndex((item) => (props.value || props.defaultValue) === item)
					: null,
			controlled: true,
			focussedMenuItemIndex: 0,
			selectedValues: props.valueIndices.map((index) => props.menu[index]),
			selectedIndices: [...props.valueIndices],
		};

		this.handleClick = this.handleClick.bind(this);
		this.handleOutsideClick = this.handleOutsideClick.bind(this);
		this.handleMenuClick = this.handleMenuClick.bind(this);
		this.handleArrowNavigation = this.handleArrowNavigation.bind(this);
	}

	componentWillUnmount() {
		document.removeEventListener('mousedown', this.handleOutsideClick, false);
		document.removeEventListener('keydown', this.handleArrowNavigation, false);
	}

	static getDerivedStateFromProps(props, state) {
		/**
		 * If the component is controlled, it takes the value passed to the props
		 * but once it regains the controll, it controls the state completely
		 */
		if (props.value !== state.selectedValue && state.controlled) {
			return {
				selectedValue: props.value,
			};
		}
		return null;
	}

	handleClick() {
		const { isOpen, onClick } = this.props;
		if (!isOpen) {
			// The dropdown is not open right now but it will be opened shortly
			// so we can think of this as the behavior when the dropdown is opened
			document.addEventListener('mousedown', this.handleOutsideClick, false);
			document.addEventListener('keydown', this.handleArrowNavigation, false);
		} else {
			// The dropdown is open at this point but we can think of this as the
			// behavior when the dropdown is closed shortly.
			document.removeEventListener('mousedown', this.handleOutsideClick, false);
			document.removeEventListener('keydown', this.handleArrowNavigation, false);
			this.setState({
				controlled: true,
				focussedMenuItemIndex: 0,
			});
		}
		onClick();
	}

	handleMenuClick(index) {
		const { menu, onSelection, multiSelect } = this.props;
		if (multiSelect) {
			this.setState(
				(prevState) => ({
					selectedValues: [...prevState.selectedValues, menu[index]],
					selectedIndices: [...prevState.selectedIndices, index],
				}),
				() => {
					const { selectedIndices } = this.state;
					onSelection(selectedIndices);
				}
			);
		} else {
			this.setState({
				selectedValue: menu[index],
				selectedIndex: index,
			});
			onSelection(index);
		}
	}

	handleOutsideClick(e) {
		// eslint-disable-next-line react/no-find-dom-node
		if (ReactDOM.findDOMNode(this).contains(e.target)) {
			return;
		}
		this.handleClick();
	}

	handleArrowNavigation(e) {
		e.stopPropagation();
		const { selectedIndices, selectedValue, controlled, focussedMenuItemIndex } = this.state;
		const { menu, isOpen, multiSelect } = this.props;
		let nextFocussedMenuItemIndex = focussedMenuItemIndex || 0;
		switch (e.code) {
			case 'ArrowDown': {
				for (let index = 0; index < menu.length; index += 1) {
					const item = menu[index];
					let isAlreadySelected = false;
					if (multiSelect) {
						isAlreadySelected = selectedIndices.includes(index);
					}
					const isCurrentlySelected = item.toLowerCase().includes(selectedValue.toLowerCase());
					if (!isAlreadySelected && (isCurrentlySelected || controlled)) {
						if (index > nextFocussedMenuItemIndex) {
							nextFocussedMenuItemIndex = index;
							break;
						}
					}
				}
				this.setState({
					focussedMenuItemIndex: nextFocussedMenuItemIndex,
				});
				break;
			}
			case 'ArrowUp': {
				for (let index = menu.length - 1; index >= 0; index -= 1) {
					const item = menu[index];
					let isAlreadySelected = false;
					if (multiSelect) {
						isAlreadySelected = selectedIndices.includes(index);
					}
					const isCurrentlySelected = item.toLowerCase().includes(selectedValue.toLowerCase());
					if (!isAlreadySelected && (isCurrentlySelected || controlled)) {
						if (index < nextFocussedMenuItemIndex) {
							nextFocussedMenuItemIndex = index;
							break;
						}
					}
				}
				this.setState({
					focussedMenuItemIndex: nextFocussedMenuItemIndex,
				});
				break;
			}
			case 'ArrowRight': {
				for (let index = menu.length - 1; index >= 0; index -= 1) {
					const item = menu[index];
					let isAlreadySelected = false;
					if (multiSelect) {
						isAlreadySelected = selectedIndices.includes(index);
					}
					const isCurrentlySelected = item.toLowerCase().includes(selectedValue.toLowerCase());
					if (!isAlreadySelected && (isCurrentlySelected || controlled)) {
						if (index < nextFocussedMenuItemIndex) {
							nextFocussedMenuItemIndex = index;
							break;
						}
					}
				}
				this.setState({
					focussedMenuItemIndex: nextFocussedMenuItemIndex,
				});
				break;
			}
			case 'Enter': {
				if (isOpen) {
					this.handleMenuClick(nextFocussedMenuItemIndex);
					this.handleClick();
				}
				break;
			}
			case 'Escape': {
				if (isOpen) this.handleClick();
				break;
			}
			default:
				break;
		}
	}

	/* eslint-disable no-nested-ternary */
	/* eslint-disable react/prop-types */

	render() {
		const {
			value,
			menu,
			menuOptions,
			menuItemOptions,
			menuItemPrefix,
			menuItemSuffix,
			inputStyle,
			isOpen,
			onClick,
			targetHover,
			prefix,
			openSuffix,
			closeSuffix,
			defaultValue,
			isLoading,
			borderColor,
			focusBorderColor,
			bg,
			focusBg,
			hoverBg,
			multiSelect,
			cursor,
			onSelection,
			DropdownButtonOptions,
			DivOptions,
			...rest
		} = this.props;
		const { selectedValues, focussedMenuItemIndex, selectedValue, selectedIndex, controlled } = this.state;
		return (
			<BaseAutocomplete
				d="flex"
				ref={(node) => {
					this.node = node;
				}}
				onClick={!isLoading ? (targetHover ? null : this.handleClick) : () => {}}
				targetHover={targetHover}
				pos="relative"
				isOpen={isOpen}
				flexWrap="wrap"
				cursor={cursor}
				{...rest}
			>
				{prefix}
				<Div
					d="flex"
					border="1px solid"
					borderColor="gray500"
					rounded="md"
					flexGrow="1"
					flexWrap="wrap"
					align="center"
					cursor={cursor}
					onClick={!isLoading ? (targetHover ? null : this.handleClick) : () => {}}
					h="1.875rem"
					{...DivOptions}
				>
					{multiSelect
						? selectedValues.map((currentValue, index) => (
								<Tag
									m={{ x: '3.2px', y: '3.2px' }}
									h="24px"
									whiteSpace="nowrap"
									onClick={(e) => {
										e.stopPropagation();
									}}
									suffix={
										<Button
											bg="transparent"
											border="none"
											p="1.6px"
											type="button"
											onClick={(e) => {
												this.setState(
													(prevState) => ({
														selectedValues: prevState.selectedValues.filter((_, _index) => _index !== index),
														selectedIndices: prevState.selectedIndices.filter((_, _index) => _index !== index),
													}),
													() => {
														const { selectedIndices } = this.state;
														onSelection(selectedIndices);
													}
												);
												e.stopPropagation();
											}}
										>
											<Icon size="12px" name="Cross" />
										</Button>
									}
								>
									{currentValue}
								</Tag>
							))
						: null}
					{selectedValue && selectedValue === value && menuItemPrefix && menuItemPrefix(selectedIndex)}
					<Input
						border="0"
						value={selectedValue}
						onFocus={() => {
							this.setState({
								selectedValue: '',
								controlled: false,
							});
						}}
						onChange={(e) => {
							this.setState({
								selectedValue: e.target.value,
								controlled: false,
							});
							if (!isOpen) {
								this.handleClick();
							}
						}}
						{...inputStyle}
					/>
					{selectedValue && selectedValue === value && menuItemSuffix && menuItemSuffix(selectedIndex)}
				</Div>
				{isLoading ? (
					<Div
						hoverBg={hoverBg}
						bg={isOpen ? focusBg : bg}
						borderColor={isOpen ? focusBorderColor : borderColor}
						alignSelf="flex-start"
						type="button"
						{...DropdownButtonOptions}
					>
						<Icon name="Loading" color="gray600" size="1rem" />
					</Div>
				) : (
					<Button
						hoverBg={hoverBg}
						bg={isOpen ? focusBg : bg}
						borderColor={isOpen ? focusBorderColor : borderColor}
						alignSelf="flex-start"
						type="button"
						{...DropdownButtonOptions}
					>
						{isOpen ? openSuffix : closeSuffix}
					</Button>
				)}
				<Div {...menuOptions}>
					{menu
						.map((item, index) => {
							const text = item;
							return (text.toLowerCase().includes(selectedValue.toLowerCase()) || controlled) &&
								!selectedValues.find((v) => v === text) ? (
								<Div
									// eslint-disable-next-line react/no-array-index-key
									key={`menu-${text}-${index}`}
									{...menuItemOptions}
									bg={focussedMenuItemIndex === index ? menuItemOptions.hoverBg : menuItemOptions.bg}
									onClick={() => this.handleMenuClick(index)}
									onMouseEnter={() => this.setState({ focussedMenuItemIndex: index })}
									onMouseLeave={() => this.setState({ focussedMenuItemIndex: undefined })}
								>
									{menuItemPrefix && menuItemPrefix(index)}
									{text}
									{menuItemSuffix && menuItemSuffix(index)}
								</Div>
							) : null;
						})
						.filter((item) => item)}
				</Div>
			</BaseAutocomplete>
		);
	}

	/* eslint-enable react/prop-types */
	/* eslint-enable no-nested-ternary */
}

/* eslint-disable react/require-default-props */
/* eslint-disable react/forbid-prop-types */

Autocomplete.propTypes = {
	value: PropTypes.string,
	valueIndices: PropTypes.array,
	defaultValue: PropTypes.string,
	// Must be wrapped around <AutocompleteMenu>
	menu: PropTypes.node,
	// Supports menu options of Div
	menuOptions: PropTypes.object,
	menuItemOptions: PropTypes.object,
	menuItemPrefix: PropTypes.func,
	menuItemSuffix: PropTypes.func,
	isOpen: PropTypes.bool,
	// change the target from click to hover
	targetHover: PropTypes.bool,
	direction: PropTypes.oneOf(['bottomleft', 'bottomright', 'topright', 'topleft', 'righttop', 'lefttop']),
	isLoading: PropTypes.bool,
	// Component typically icon placed before the component
	prefix: PropTypes.node,
	// Component (icon) placed at the end of the component when is open
	openSuffix: PropTypes.node,
	// Component (icon) placed at the end of the component when is closed
	closeSuffix: PropTypes.node,
	onClick: PropTypes.func,
	onSelection: PropTypes.func,
	multiSelect: PropTypes.bool,
	DropdownButtonOptions: PropTypes.shape({}),
	inputStyle: PropTypes.shape({}),
};

/* eslint-enable react/forbid-prop-types */
/* eslint-enable react/require-default-props */

/* eslint-disable react/default-props-match-prop-types */

Autocomplete.defaultProps = {
	tag: 'div',
	d: 'flex',
	align: 'center',
	textColor: 'medium',
	textWeight: '500',
	justify: 'space-between',
	p: '0',
	maxH: '100%',
	textSize: 'body',
	rounded: 'md',
	border: 'none',
	borderColor: 'gray500',
	w: '100%',
	bg: 'gray100',
	focusBg: 'gray200',
	focusBorderColor: 'gray800',
	hoverBg: 'gray100',
	cursor: 'pointer',
	onClick: () => {},
	onSelection: () => null,
	defaultValue: 'Please select a value',
	value: null,
	openSuffix: <Icon name="UpArrow" size="18px" />,
	closeSuffix: <Icon name="DownArrow" size="18px" />,
	menuOptions: {
		pos: 'absolute',
		shadow: '4',
		minW: '100%',
		rounded: 'md',
		bg: 'white',
	},
	DropdownButtonOptions: {
		p: '7px',
	},
	DivOptions: {},
	menuItemOptions: {
		p: { x: '16px', y: '8px' },
		hoverBg: 'gray100',
		bg: 'white',
	},
	menuItemPrefix: () => null,
	menuItemSuffix: () => null,
	multiSelect: false,
	valueIndices: [],
	inputStyle: {},
};

/* eslint-enable react/default-props-match-prop-types */

export default withTheme(Autocomplete);
